import React, { useMemo } from 'react';
import { Box, Text, useColorModeValue } from '@chakra-ui/react';
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Bar,
    Line,
    Scatter,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
import { formatNumber } from '../produksi/utils';
import { RawDatum } from './CpoTrend';
import { CustomTooltipDaily } from '../CustomTooltip';

export interface CPOHarianDatum extends RawDatum {
    date: string;
}

interface Props {
    data: CPOHarianDatum[];
}

const COLORS = {
    Produksi: '#82CA9D',
    OER: '#8884D8',
    Stok: '#CA6060',
    Black: '#000000',
    Kirim: '#FFC658',
    jamOlah: '#0088FE',
};

const CPOProduksiHarian: React.FC<Props> = ({ data }) => {
    const [hoveringDataKey, setHoveringDataKey] = React.useState<DataKey<any> | undefined>(undefined);

    let stokOpacity = 1;
    let produksiOpacity = 1;
    let kirimOpacity = 1;
    let oerOpacity = 1;

    if (hoveringDataKey === 'Stok') {
        produksiOpacity = 0.5;
        kirimOpacity = 0.5;
        oerOpacity = 0.5;
    } else if (hoveringDataKey === 'Produksi') {
        stokOpacity = 0.5;
        kirimOpacity = 0.5;
        oerOpacity = 0.5;
    } else if (hoveringDataKey === 'Kirim') {
        stokOpacity = 0.5;
        produksiOpacity = 0.5;
        oerOpacity = 0.5;
    } else if (hoveringDataKey === 'OER') {
        stokOpacity = 0.5;
        produksiOpacity = 0.5;
        kirimOpacity = 0.5;
    }

    const handleMouseEnter = (payload: any) => {
        setHoveringDataKey(payload.dataKey);
    };

    const handleMouseLeave = () => {
        setHoveringDataKey(undefined);
    };
    const formattedData = useMemo(() => {
        // pivot raw API → wide format per date
        const tmp: Record<string, any> = {};
        data.forEach(({ date, descr, qty }) => {
            if (!tmp[date]) {
                tmp[date] = { date, Stok: 0, Produksi: 0, Kirim: 0, OER: 0, jamOlah: 0 };
            }
            switch (descr) {
                case 'Stok':
                    tmp[date].Stok = qty/1000;
                    break;
                case 'Produksi':
                    tmp[date].Produksi = qty/1000;
                    break;
                case 'Kirim':
                    tmp[date].Kirim = qty/1000;
                    break;
                case '% OER':
                    tmp[date].OER = qty;
                    break;
                case 'Jam Olah':
                    tmp[date].jamOlah = qty;
                    break;
            }
        });
        return Object.values(tmp).sort(
            (a, b) => parseInt(a.date, 10) - parseInt(b.date, 10)
        );
    }, [data]);

    // fallback ke bulan/tahun sekarang jika data kosong
    const now = new Date();
    const defaultYear = now.getFullYear().toString();
    const defaultMonthNum = String(now.getMonth() + 1).padStart(2, '0');

    const rawPeriod = data[0]?.periode;
    const year = rawPeriod ? rawPeriod.slice(0, 4) : defaultYear;
    const monthNum = rawPeriod ? rawPeriod.slice(4) : defaultMonthNum;

    const monthNames = [
        'Januari','Februari','Maret','April','Mei','Juni',
        'Juli','Agustus','September','Oktober','November','Desember'
    ];
    const monthName = monthNames[parseInt(monthNum, 10) - 1];

    const bgColor = useColorModeValue('white', 'gray.800');
    const titleColor = useColorModeValue('gray.700', 'gray.200');

    return (
        <Box p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" overflowX="auto" overflowY="hidden">
            <Text 
                fontSize="lg" 
                fontWeight="bold" 
                mb={4} 
                color={titleColor}
            >
                Produksi CPO Harian ({monthName} {year})
            </Text>
            <Box
                // flex={1} 
                minH="130px" 
                height="350px"
                minWidth="700px"
                overflowX="auto" 
                overflowY="hidden"
                w="100%"
            >
                <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={formattedData} barGap={0}>
                    <CartesianGrid 
                        strokeDasharray="3 3" 
                        stroke={useColorModeValue('#e2e8f0', '#4a5568')} 
                    />
                    <XAxis
                        dataKey="date"
                        // interval={0}
                        height={80}
                        tick={{ fontSize: 12 }}
                    />

                    {/* YAxis kiri untuk ton */}
                    <YAxis
                        yAxisId="left"
                        tickFormatter={v => formatNumber(Number(v), 'number')}
                        tick={{ fontSize: 12 }}
                        label={{
                            value: 'Ton',
                            angle: -90,
                            position: 'insideLeft',
                            offset: 5
                        }}
                    />

                    {/* YAxis kanan untuk jam & persen */}
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        // axisLine={{ stroke: useColorModeValue('#cbd5e0', '#4a5568') }}
                        tickFormatter={v =>
                            typeof v === 'number'
                                ? `${v}${v <= 24 ? '' : ''}`
                                : v
                        }
                        label={{
                            value: 'Jam / %',
                            angle: 90,
                            position: 'insideRight',
                            offset: 20
                        }}
                    />

                    <Tooltip
                        content={<CustomTooltipDaily />}
                        formatter={(val, name) =>
                            name === 'jamOlah'
                                ? `${val} Jam`
                                : name === 'OER'
                                ? `${(val as number).toFixed(2)}%`
                                : formatNumber(Number(val), 'number')
                        }
                        wrapperStyle={{ zIndex: 1000 }}
                    />
                    <Legend 
                        height={30}
                        wrapperStyle={{ bottom: 60 }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    />

                    {/* Bar untuk Stok */}
                    {/* <Bar 
                        yAxisId="left" 
                        name="Stok" 
                        dataKey="Stok" 
                        stackId="a" 
                        fill={COLORS.Stok}
                        fillOpacity={stokOpacity}
                        radius={[0, 0, 0, 0]}
                    /> */}
                    <Bar 
                        yAxisId="left" 
                        name="Produksi" 
                        dataKey="Produksi" 
                        stackId="a" 
                        fill={COLORS.Produksi}
                        fillOpacity={produksiOpacity}
                        radius={[0, 0, 0, 0]}
                    />
                    <Bar 
                        yAxisId="left" 
                        name="Kirim" 
                        dataKey="Kirim" 
                        fill={COLORS.Kirim}
                        fillOpacity={kirimOpacity}
                        radius={[2, 2, 0, 0]}
                    />

                    {/* Line untuk % OER */}
                    <Line
                        yAxisId="right"
                        name="OER"
                        type="linear"
                        dataKey="OER"
                        stroke={COLORS.OER}
                        strokeWidth={2}
                        strokeOpacity={oerOpacity}
                        dot={{ r: 7 }}
                        activeDot={{ r: 7 }}
                    />

                    {/* Scatter untuk Jam Olah */}
                    <Scatter
                        yAxisId="right"
                        name="Jam Olah"
                        dataKey="jamOlah"
                        fill={COLORS.Black}
                        shape="cross"
                    />
                </ComposedChart>
            </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default CPOProduksiHarian;